import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import { Button, TEOLoader } from '@teo/components';
import {
  ArrowLeft,
  Shapes,
  TimeFilled,
  TaskCheckmarkFilledNew,
} from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { nthLastIndexOf, convertToTime, useMobile } from '@/util';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  sortDataLP,
  applyCompetenceSkips,
} from '@/components/LearningPath/util';
import ConnectSection from './ConnectSection';
import { NavContext } from '@/components/NavContext';
import { LearnContent } from '@/components/LearningPath/Learn/LearnContent';
import { LpProgress } from '@/components/LearningPath/Learn/LpProgress';
import { axios } from '@/axios';
import { config } from '@/config';
import InfoSection from '@/components/InfoSection';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';

const MilestoneActive = ({ activeItem, auth, basePath }: any) => {
  const navigate = useNavigate();
  useGet(`/trainingfollow/${activeItem?.objectId}/${auth?.user?.id}`, {
    enabled: activeItem?.subType === 'milestone',
    retry: false,
    onSuccess: async (res: any) => {
      navigate(`${basePath}/milestones/${activeItem?.objectId}`);
    },
    onError: (res: any) => {
      const promises = [];
      promises.push(
        axios.post(`${config.backend}/trainingfollows`, {
          trainingId: activeItem?.objectId,
          userId: auth?.user?.id,
        })
      );
      Promise.allSettled(promises).then(() => {
        navigate(`${basePath}/milestones/${activeItem?.objectId}`);
      });
    },
  });
  return <></>;
};

const ResumeLearningPath = ({ activeItem, auth, follow }: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isUserFollow, setIsUserFollow] = useState(true);

  const originPath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const basePath = window.location.pathname;

  useGet(`/follow/${activeItem?.objectId}/${auth?.user?.id}`, {
    enabled: activeItem?.type === 'course',
    retry: false,
    onSuccess: async () => {
      setIsUserFollow(true);
    },
    onError: (res: any) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  useGet(`/trainingfollow/${activeItem?.objectId}/${auth?.user?.id}`, {
    enabled: activeItem?.type === 'training',
    retry: false,
    onSuccess: async () => {
      setIsUserFollow(true);
    },
    onError: (res: any) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  const resumeLP = () => {
    {
      if (activeItem?.type === 'course') {
        if (!isUserFollow) {
          const promises = [];
          promises.push(
            axios.post(`${config.backend}/follows`, {
              courseId: activeItem?.objectId,
              userId: auth?.user?.id,
              role: 20,
            })
          );
          Promise.allSettled(promises).then(() => {
            navigate(
              `${originPath}/${
                activeItem?.subType === 'assessment'
                  ? 'assessments'
                  : activeItem?.subType === 'assessment'
                  ? 'vacancies'
                  : 'courses'
              }/${activeItem?.objectId}`
            );
          });
        } else {
          navigate(
            `${originPath}/${
              activeItem?.subType === 'assessment'
                ? 'assessments'
                : activeItem?.subType === 'assessment'
                ? 'vacancies'
                : 'courses'
            }/${activeItem?.objectId}`
          );
        }
      }

      if (activeItem?.type === 'training') {
        if (!isUserFollow) {
          const promises = [];
          promises.push(
            axios.post(`${config.backend}/trainingfollows`, {
              trainingId: activeItem?.objectId,
              userId: auth?.user?.id,
            })
          );
          Promise.allSettled(promises).then(() => {
            navigate(
              `${basePath}/${
                activeItem?.subType === 'milestone'
                  ? 'milestones'
                  : activeItem?.subType === 'evaluation'
                  ? 'evaluations'
                  : 'trainings'
              }/${activeItem?.objectId}`
            );
          });
        } else {
          navigate(
            `${basePath}/${
              activeItem?.subType === 'milestone'
                ? 'milestones'
                : activeItem?.subType === 'evaluation'
                ? 'evaluations'
                : 'trainings'
            }/${activeItem?.objectId}`
          );
        }
      }
      activeItem?.type === 'learningpath' &&
        navigate(`${basePath}/themes/${activeItem?.objectId}`);
    }
  };

  return (
    <div className="my-8">
      <Button
        disabled={follow?.isBlocked}
        onClick={() => {
          resumeLP();
        }}
      >
        {t('pages.lp_landing.btn_resume')}
      </Button>
    </div>
  );
};

const LpInfo = ({ lpGroupInfo }: any) => {
  const combinedSubInfo = lpGroupInfo?.subTitles.map(
    (title: any, index: any) => ({
      title,
      text: lpGroupInfo?.subTexts[index],
      image: lpGroupInfo?.subImages[index],
    })
  );

  return (
    <div className="mt-8 flex flex-col border-t pt-4">
      {combinedSubInfo?.map((info: any, index: any) => {
        return (
          <div
            key={index}
            className="my-4 flex w-full flex-col gap-2 px-4 sm:px-0"
          >
            {info?.image && (
              <div>
                <img
                  src={info?.image}
                  className="w-full rounded-md object-cover"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = '/assets/images/opleidingen.jpg';
                  }}
                />
              </div>
            )}
            {info?.title && (
              <h3 className="text-xl text-grey-08">{info?.title}</h3>
            )}
            {info?.text && (
              <p
                className="text-sm text-grey-08 sm:text-base"
                dangerouslySetInnerHTML={{ __html: info?.text }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

function LearningPathDetail() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { auth } = useContext(AuthContext);
  const { lpgroupId, organisationId } = useParams();
  const [lpGroup, setLpGroup] = useState<any>(null);
  const [lpGroupInfo, setLpGroupInfo] = useState(null);
  const [follow, setFollow] = useState<any>(null);
  const [nodes, setNodes] = useState<any>(null);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [allNodes, setAllNodes] = useState<any>(null);
  const [isUserFollow, setIsUserFollow] = useState<any>(false);
  const [selectNode, setSelectNode] = useState(null);
  const [isError, setIsError] = useState<any>(false);
  const [lastCompleted, setLastCompleted] = useState<any>(null);
  const basePath = window.location.pathname;
  const originPath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 4)
  );

  const { setLeftSidebar, setHeaderDisabled, setSubPage } =
    useContext(NavContext);
  const [allCategories, setAllCategories] = useState([]);
  const [includeCertificate, setIncludeCertificate] = useState(null);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    setLeftSidebar(true);
    return () => setLeftSidebar(false);
  }, []);

  useEffect(() => {
    setHeaderDisabled(true);
    return () => setHeaderDisabled(false);
  }, []);

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useGet(`/lpgroupdetail/${lpgroupId}`, {
    retry: false,
    onSuccess: (result: any) => {
      setLpGroup(result?.data || {});
    },
    onError: (res: any) => {
      setIsError(true);
    },
  });

  useGet(`/lpinfo/${lpgroupId}`, {
    retry: false,
    onSuccess: (result: any) => {
      setLpGroupInfo(result?.data || {});
    },
  });

  useGet(`/lpfollow/${lpgroupId}/${auth?.user?.id}`, {
    retry: false,
    onSuccess: (result: any) => {
      setFollow(result?.data || {});
    },
    onError: (res: any) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  useGet(`/lpnodestatuses`, {
    enabled: !!lpGroup,
    lpgroupId: lpgroupId,
    userId: auth?.user?.id,
    onSuccess: async (res: any) => {
      const nodedetails = applyCompetenceSkips(
        res?.data?.filter(
          (el: any) =>
            el?.type !== 'root' &&
            el?.learningpathId === lpGroup?.learningpathId
        )
      );
      if (nodedetails?.length > 0) {
        const sortData = sortDataLP(nodedetails);
        setNodes(sortData);

        let lastActive;

        const checkLastActive = sortData?.find(
          (nds: any) => nds.progress !== 1 && (nds?.mandatory || nds?.blocking)
        );

        if (checkLastActive?.subType === 'milestone') {
          const allBefore = getAllBeforeById(sortData, checkLastActive?.id);
          const allOk = areAllPassed(allBefore);
          if (allOk) {
            lastActive = checkLastActive;
          } else {
            lastActive = sortData?.find(
              (nds: any) =>
                nds.progress !== 1 &&
                nds?.mandatory &&
                nds?.subType !== 'milestone'
            );
          }
        } else {
          lastActive = checkLastActive;
        }

        const notCorrectedActive = sortData?.find(
          (nds: any) =>
            nds.progress === 1 &&
            nds?.mandatory &&
            nds?.subType !== 'milestone' &&
            !nds.isCorrected
        );

        const notPassedActive = sortData?.find(
          (nds: any) =>
            nds.progress === 1 &&
            nds?.mandatory &&
            nds?.subType !== 'milestone' &&
            nds.isCorrected &&
            nds.maxScore > 0 &&
            nds.score / nds.maxScore < nds.minScore
        );

        setActiveItem(
          lastActive
            ? lastActive
            : notCorrectedActive
            ? notCorrectedActive
            : notPassedActive
            ? notPassedActive
            : sortData[0]
        );

        setLastCompleted(
          sortData
            .slice()
            .reverse()
            .find((el) => el.progress === 1)
        );

        const mlsCertificate = sortData?.find(
          (el) => el?.subType === 'milestone' && el?.hasCertificate
        );
        setIncludeCertificate(mlsCertificate);
        setLoader(false);
      } else {
        setLoader(false);
      }
    },
    onError: (res: any) => {
      console.error('res', res);
      setLoader(false);
    },
  });

  useGet(`/learningpathnodedetailsflat`, {
    enabled: !!lpGroup,
    topLearningpathId: lpGroup?.learningpathId,
    onSuccess: async (res: any) => {
      const lpAllNodes = res?.data?.filter((nds: any) => nds?.type !== 'root');
      setAllNodes(lpAllNodes);
    },
  });

  useEffect(() => {
    if (lpGroup) {
      axios.post(config.backend + '/activity', {
        userId: auth?.user?.id,
        subjectId: lpGroup?.id,
        activityType: 'learnPath',
        subject: lpGroup.lpgroupName || lpGroup.lpName,
        logo: lpGroup.lpgroupPicture || lpGroup.lpPicture,
        link: window.location.pathname,
      });
    }
  }, [lpGroup]);

  useGet(`/categories`, {
    sort: 'tag',
    organisationId: `${auth?.user?.activeOrganisationId},OR,organisationId=null`,
    onSuccess: (result: any) => {
      setAllCategories(
        result?.data?.map((el: any) => ({
          id: el.id,
          label: el.tag,
          icon: el.icon,
        })) || []
      );
    },
  });

  useEffect(() => {
    if (isError) {
      return navigate(`${originPath}/noaccess`);
    }
  }, [isError]);

  const handleGoBack = () => {
    const lpPath = basePath.slice(0, basePath.lastIndexOf('/'));
    navigate(`${lpPath}`);
  };

  function getAllBeforeById(items: any, nodeId: any) {
    const byId = new Map(items.map((item: any) => [item.id, item]));
    const start = items.find((item: any) => item.id === nodeId);

    if (!start) return [];

    const result: any = [];
    const visited = new Set();

    function walk(node: any) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) continue;
        visited.add(parentId);

        const parent = byId.get(parentId);
        if (parent) {
          result.push(parent);
          walk(parent);
        }
      }
    }

    walk(start);
    return result;
  }

  // if (!activeItem) return null;

  function areAllPassed(arr: any) {
    return arr.every((item: any) => {
      if (item?.skippedByCompetences) return true;
      const hasProgress = item.progress === 1;
      const hasScore =
        item.maxScore === 0 && item.score === 0 && item?.isCorrected
          ? true
          : item.minScore > 0
          ? item.score / item.maxScore >= item?.minScore
          : true;
      return hasProgress && hasScore && item?.isCorrected;
    });
  }

  if (activeItem?.subType === 'milestone') {
    const allBefore = getAllBeforeById(nodes, activeItem?.id);
    const allOk = areAllPassed(allBefore);

    if (allOk) {
      return (
        <MilestoneActive
          activeItem={activeItem}
          auth={auth}
          basePath={basePath}
        />
      );
    }
  }

  return (
    <div className="-mx-4 flex flex-col">
      {loader ? (
        <div className="fixed inset-0 z-[101] flex bg-white/75">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[120px]"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="relative -mt-4 h-[640px] w-full overflow-hidden bg-grey-01">
            {auth && (
              <>
                {isMobile ? (
                  <div className="bg-white px-4 pt-4">
                    <MobileSubPageMenu backFn={() => handleGoBack()} />
                  </div>
                ) : (
                  <Button
                    className="absolute top-5 left-5 z-[1] flex items-center gap-3"
                    variant="text"
                    onClick={() => handleGoBack()}
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <p>{t('pages.lp_landing.back_to_lp')}</p>
                  </Button>
                )}
              </>
            )}
            {nodes?.length > 0 && (
              <ConnectSection
                lpGroup={lpGroup}
                items={nodes}
                activeItem={activeItem}
                allCategories={allCategories}
                setSelectNode={setSelectNode}
                selectNode={selectNode}
                auth={auth}
                follow={follow}
              />
            )}
          </div>
          <div className="my-8 mx-auto flex w-full max-w-[720px] flex-col px-0 sm:px-4">
            {follow?.isBlocked && (
              <div className="py-8">
                <InfoSection text={t('pages.lp_landing.notPassLP')} />
              </div>
            )}
            {!follow?.isBlocked &&
              lpGroup?.order === 'fixed' &&
              lastCompleted?.notPassed && (
                <>
                  {lastCompleted?.isCorrected ? (
                    lastCompleted.maxScore > 0 &&
                    lastCompleted.score / lastCompleted.maxScore <
                      lastCompleted.minScore && (
                      <div className="py-8">
                        <InfoSection
                          text={t('pages.lp_landing.notPass', {
                            subType: lastCompleted?.subType,
                            name: lastCompleted?.name,
                          })}
                        />
                      </div>
                    )
                  ) : (
                    <div className="py-8">
                      <InfoSection
                        text={t('pages.lp_landing.notCorect', {
                          subType: lastCompleted?.subType,
                          name: lastCompleted?.name,
                        })}
                      />
                    </div>
                  )}
                </>
              )}
            <div className="flex w-full flex-col border-b px-4 sm:px-0">
              <p className="text-sm text-grey-05">{t('pages.lp_landing.lp')}</p>
              <h3 className="mt-1 text-xl">{lpGroup?.lpgroupName}</h3>
              <div className="my-4 flex items-center">
                {activeItem && allNodes && (
                  <LpProgress
                    allNodes={nodes}
                    lpGroup={lpGroup}
                    activeItem={activeItem}
                    follow={follow}
                  />
                )}
              </div>
              {lpGroup?.lpgroupDescription && (
                <p className="text-grey-08">{lpGroup?.lpgroupDescription}</p>
              )}

              <div className="mt-4 flex gap-4">
                {allNodes?.length > 0 && (
                  <div className="flex items-center">
                    <Shapes className="h-4 w-4 text-grey-04" />
                    <p className="pl-1 text-sm text-grey-05">
                      {allNodes?.length} {t('pages.lp_landing.items')}
                    </p>
                  </div>
                )}

                {/*lpGroup?.lpEstimatedTime > 0 && (
                  <div className="flex items-center">
                    <TimeFilled className="h-4 w-4 text-grey-04" />
                    <p className="pl-1 text-sm text-grey-05">
                      {convertToTime(
                        Math.abs(lpGroup?.lpEstimatedTime),
                        t('pages.learn_index.hour'),
                        t('pages.learn_index.minute')
                      )}
                    </p>
                  </div>
                )*/}

                {includeCertificate && (
                  <div className="flex items-center">
                    <TaskCheckmarkFilledNew className="h-4 w-4 text-grey-04" />
                    <p className="pl-1 text-sm text-grey-05">
                      {t('pages.landing_training.certificate')}
                    </p>
                  </div>
                )}
              </div>
              <ResumeLearningPath
                activeItem={activeItem}
                auth={auth}
                follow={follow}
              />
            </div>
            {allNodes?.length > 0 && (
              <div className="mt-8 px-4 sm:px-0">
                <h3 className="mb-4 text-base">
                  {t('pages.lp_landing.this_lp')}
                </h3>
                <LearnContent allNodes={allNodes} />
              </div>
            )}

            {lpGroupInfo && <LpInfo lpGroupInfo={lpGroupInfo} />}
          </div>
        </>
      )}
    </div>
  );
}

export default LearningPathDetail;
